/**
 * Printer Permission Utility for Capacitor/Android
 * Handles runtime permission requests for Bluetooth printing on Android 6+
 * Uses cordova-plugin-android-permissions when available
 */

declare global {
  interface Window {
    cordova?: {
      plugins?: {
        permissions?: {
          ACCESS_FINE_LOCATION: string;
          ACCESS_COARSE_LOCATION: string;
          BLUETOOTH_CONNECT?: string;
          BLUETOOTH_SCAN?: string;
          CAMERA?: string;
          requestPermission: (
            permission: string,
            success: (status: { hasPermission: boolean }) => void,
            error: () => void
          ) => void;
          checkPermission: (
            permission: string,
            success: (status: { hasPermission: boolean }) => void,
            error: () => void
          ) => void;
        };
      };
    };
  }
}

export interface PrinterPermissionResult {
  granted: boolean;
  permission: string;
  error?: string;
}

export interface BluetoothPermissionStatus {
  locationFine: boolean;
  locationCoarse: boolean;
  bluetoothConnect: boolean;
  bluetoothScan: boolean;
  allGranted: boolean;
}

const STORAGE_KEY_PERMISSION_LOGGED = 'bluetooth_printer_permission_logged';

/**
 * Check if cordova permissions plugin is available
 */
export function isPermissionsPluginAvailable(): boolean {
  return typeof window !== 'undefined' && 
    !!window.cordova?.plugins?.permissions;
}

/**
 * Request a single permission (Android)
 */
export function requestPermission(permission: string): Promise<PrinterPermissionResult> {
  return new Promise((resolve) => {
    if (typeof window === 'undefined' || !window.cordova?.plugins?.permissions) {
      resolve({ granted: false, permission, error: 'Permissions plugin not available' });
      return;
    }

    const perms = window.cordova.plugins.permissions;
    perms.requestPermission(
      permission,
      (status) => resolve({ granted: !!status.hasPermission, permission }),
      () => resolve({ granted: false, permission, error: 'Request failed' })
    );
  });
}

/**
 * Check if a permission is granted
 */
export function checkPermission(permission: string): Promise<boolean> {
  return new Promise((resolve) => {
    if (typeof window === 'undefined' || !window.cordova?.plugins?.permissions) {
      resolve(false);
      return;
    }

    const perms = window.cordova.plugins.permissions;
    perms.checkPermission(
      permission,
      (status) => resolve(!!status.hasPermission),
      () => resolve(false)
    );
  });
}

/**
 * Request all Bluetooth-related permissions for printing
 * Order: Location (Android <12) -> BLUETOOTH_CONNECT -> BLUETOOTH_SCAN
 */
export async function requestBluetoothPermissions(): Promise<BluetoothPermissionStatus> {
  const status: BluetoothPermissionStatus = {
    locationFine: false,
    locationCoarse: false,
    bluetoothConnect: false,
    bluetoothScan: false,
    allGranted: false,
  };

  if (!isPermissionsPluginAvailable()) {
    console.warn('[PrinterPermission] Cordova permissions plugin not available');
    return status;
  }

  const perms = window.cordova!.plugins!.permissions!;

  // 1. Location (required for Bluetooth scanning on Android < 12)
  const locFine = await requestPermission(perms.ACCESS_FINE_LOCATION);
  status.locationFine = locFine.granted;

  const locCoarse = await requestPermission(perms.ACCESS_COARSE_LOCATION);
  status.locationCoarse = locCoarse.granted;

  // 2. Android 12+ Bluetooth permissions (cordova-plugin-android-permissions 1.1.5+)
  const btConnectPerm = perms.BLUETOOTH_CONNECT ?? 'android.permission.BLUETOOTH_CONNECT';
  const btScanPerm = perms.BLUETOOTH_SCAN ?? 'android.permission.BLUETOOTH_SCAN';

  const btConnect = await requestPermission(btConnectPerm);
  status.bluetoothConnect = btConnect.granted;

  const btScan = await requestPermission(btScanPerm);
  status.bluetoothScan = btScan.granted;

  status.allGranted = (status.locationFine || status.bluetoothConnect) && status.bluetoothConnect && status.bluetoothScan;
  return status;
}

/**
 * Request permissions before Bluetooth scan - call this before listBluetoothDevices
 */
export async function ensureBluetoothPermissions(): Promise<boolean> {
  if (!isPermissionsPluginAvailable()) {
    return true; // Web or plugin not loaded - proceed
  }

  const status = await requestBluetoothPermissions();
  return status.allGranted;
}

/**
 * Get user-friendly error message for permission denial
 */
export function getPermissionDeniedMessage(): string {
  return 'Bluetooth permission denied. Please enable Bluetooth and Location access in app settings to use printer features.';
}
